import { useState } from 'react';

import { DataTable, Paginator, Chip, FilterBar } from 'components';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import { displayDateTime } from '../DirectClaims/format';
import { outcomeColor, statusColor } from './format';
import {
  GlobalApproval,
  GlobalApprovalsParams,
} from './GlobalApprovals.types';

import './global-approvals.scss';

// The values GLOBAL uses, which the service also checks against.
const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  ...[
    'Working On The Approval',
    'Pending Client Final Clearance',
    'Pending Marketing Decision',
    'Closed',
  ].map((value) => ({ label: value, value })),
];

const TYPE_OPTIONS = [
  { label: 'All types', value: '' },
  ...[
    'Laboratory and X Ray',
    'IN Patient',
    'ER',
    'Medicines',
    'Physiotherapy',
    'Maternity',
    'Doctor Visit',
    'Chronic Cases',
    'Optical',
    'Dental',
    'Other',
  ].map((value) => ({ label: value, value })),
];

type ApprovalRow = GlobalApproval & { key: string };

interface GlobalApprovalsUiProps {
  approvals: GlobalApproval[];
  totalRecords: number;
  loading: boolean;
  errorMessage: string;
  search: string;
  params: GlobalApprovalsParams;
  dates: (Date | null)[];
  handlers: {
    handleSearchChange: (name: string, value: string) => void;
    handleSubmitSearch: () => void;
    handleDateChange: (event: any) => void;
    handleStatusChange: (value: string) => void;
    handleTypeChange: (value: string) => void;
    handlePageChange: (event: any) => void;
  };
}

function referenceBody(approval: GlobalApproval) {
  return (
    <div className="global-approvals__stacked global-approvals__reference">
      <span className="global-approvals__strong">
        {approval.reference || '-'}
      </span>
      {approval.globalReference && (
        <span className="global-approvals__muted">
          {approval.globalReference}
        </span>
      )}
    </div>
  );
}

function memberBody(approval: GlobalApproval) {
  const details = [approval.memberId, approval.relationship]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="global-approvals__stacked">
      <span className="global-approvals__strong">
        {approval.memberName || '-'}
      </span>
      {details && <span className="global-approvals__muted">{details}</span>}
    </div>
  );
}

function statusBody(approval: GlobalApproval) {
  if (!approval.status) return '-';

  return (
    <div className="global-approvals__stacked global-approvals__status">
      <Chip label={approval.status} color={statusColor(approval)} />
      {approval.outcome && (
        <Chip label={approval.outcome} color={outcomeColor(approval)} />
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="global-approvals__detail">
      <span className="global-approvals__detail-label">{label}</span>
      <span>
        {value === null || value === undefined || value === '' ? '-' : value}
      </span>
    </div>
  );
}

function ApprovalExpansion(approval: GlobalApproval) {
  return (
    <div className="global-approvals__details">
      {/* Every row is an AZADEA company, so which one sits here rather than
          taking a column. */}
      <Detail label="Company" value={approval.company} />
      <Detail label="Group" value={approval.group} />
      {/* Only for a dependant; for the principal it would repeat the row. */}
      {approval.principalName &&
        approval.principalName !== approval.memberName && (
          <Detail label="Principal member" value={approval.principalName} />
        )}
      <Detail label="Policy" value={approval.policy} />
      <Detail label="Country" value={approval.country} />
      <Detail label="TPA" value={approval.tpa} />
      <Detail label="Last update" value={displayDateTime(approval.updatedAt)} />
      <Detail label="Request" value={approval.request} />
      <Detail label="Decision" value={approval.decision} />
      <Detail label="Medical remark" value={approval.medicalRemark} />
    </div>
  );
}

const COLUMNS = [
  { expander: true, field: 'expander', style: { width: '3rem' } },
  { header: 'Approval', field: 'reference', body: referenceBody },
  { header: 'Member', field: 'memberName', body: memberBody },
  {
    header: 'Type',
    field: 'type',
    body: (approval: GlobalApproval) => approval.type || '-',
  },
  {
    header: 'Provider',
    field: 'provider',
    body: (approval: GlobalApproval) => approval.provider || '-',
  },
  {
    header: 'Request date',
    field: 'requestDate',
    body: (approval: GlobalApproval) => displayDateTime(approval.requestDate),
  },
  { header: 'Status', field: 'status', body: statusBody },
];

export default function GlobalApprovalsUi({
  approvals,
  totalRecords,
  loading,
  errorMessage,
  search,
  params,
  dates,
  handlers,
}: GlobalApprovalsUiProps) {
  const [expandedRows, setExpandedRows] = useState<any>(null);

  // GLOBAL keeps a few exact duplicates and no id of its own, so the row's
  // place in the list keeps the key unique.
  const rows: ApprovalRow[] = approvals.map((approval, index) => ({
    ...approval,
    key: `${approval.reference}-${params.first + index}`,
  }));

  return (
    <div className="global-approvals">
      <FilterBar
        search={{
          inputs: [
            {
              name: 'search',
              label: 'Search',
              placeholder: 'Member, member ID, approval, policy or provider',
              value: search,
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSubmitSearch,
          width: 5,
        }}
        filters={[
          {
            kind: 'date',
            name: 'dates',
            placeholder: 'Request date range',
            // Day first, matching the dates in the table.
            dateFormat: 'dd/mm/yy',
            value: dates,
            onChange: handlers.handleDateChange,
            width: 3,
          },
          {
            kind: 'dropdown',
            name: 'type',
            placeholder: 'Type',
            value: params.type,
            options: TYPE_OPTIONS,
            onChange: (event: any) => handlers.handleTypeChange(event.value),
            width: 2,
          },
          {
            kind: 'dropdown',
            name: 'status',
            placeholder: 'Status',
            value: params.status,
            options: STATUS_OPTIONS,
            onChange: (event: any) => handlers.handleStatusChange(event.value),
            width: 2,
          },
        ]}
      />

      {errorMessage && (
        <p className="global-approvals__error" role="alert">
          {errorMessage}
        </p>
      )}

      <DataTable
        value={loading ? [] : rows}
        columns={COLUMNS as any}
        dataKey="key"
        totalRecords={loading ? 0 : totalRecords}
        expandedRows={expandedRows}
        onRowToggle={(event: any) => setExpandedRows(event.data)}
        rowExpansionTemplate={ApprovalExpansion}
        emptyMessage={
          loading ? 'Loading...' : 'No approvals match these filters.'
        }
        stripedRows
      />

      {!loading && totalRecords > 0 && (
        <Paginator
          first={params.first}
          rows={params.rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={handlers.handlePageChange}
        />
      )}
    </div>
  );
}
